import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Box, CircularProgress,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useLazyGetParticipationInfoQuery,
  useJoinHatimByCodeMutation,
} from "../../../api/hatimApi";
import PageRangeEditor from "./PageRangeEditor";
import { SoftButton, useDialogPaperSx, useIsDark } from "../soft/SoftKit";

const CODE_RE = /^[A-Z0-9]{8}$/;

function isRowValid(row, maxPage) {
  const s = Number(row.startPage);
  const e = Number(row.endPage);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
  if (s < 1 || e < 1 || s > e) return false;
  if (maxPage > 0 && (s > maxPage || e > maxPage)) return false;
  return true;
}

export default function JoinHatimByCodeModal({ open, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const paperSx = useDialogPaperSx();
  const dark = useIsDark();
  const [raw, setRaw] = useState("");
  const [amount, setAmount] = useState("");
  const [rows, setRows] = useState([]);
  const [submitError, setSubmitError] = useState("");

  const code = useMemo(() => raw.trim().toUpperCase(), [raw]);
  const codeValid = CODE_RE.test(code);

  const [trigger, { data: lookup, isFetching, isError: lookupError }] =
    useLazyGetParticipationInfoQuery();
  const [joinByCode, { isLoading: joinLoading }] = useJoinHatimByCodeMutation();

  useEffect(() => {
    if (!codeValid) return;
    const handle = setTimeout(() => {
      trigger({ code });
    }, 200);
    return () => clearTimeout(handle);
  }, [code, codeValid, trigger]);

  useEffect(() => {
    if (!open) {
      setRaw("");
      setAmount("");
      setRows([]);
      setSubmitError("");
    }
  }, [open]);

  const info = lookup?.data;
  const isRange = !!info?.isRangeType;
  const maxPage = Number(info?.totalAmount || 0);

  const amountNum = Number(amount);
  const amountValid =
    String(amount).trim() !== "" && Number.isFinite(amountNum) && amountNum >= 1;
  const rangesValid = rows.length > 0 && rows.every((r) => isRowValid(r, maxPage));
  const canJoin =
    codeValid &&
    !!info &&
    !lookupError &&
    !joinLoading &&
    (isRange ? rangesValid : amountValid);

  const helperText = !raw
    ? t("hatim_join_code_help")
    : !codeValid
    ? t("hatim_join_code_invalid_format")
    : lookupError
    ? t("hatim_join_code_lookup_error")
    : t("hatim_join_code_help");

  const handleSubmit = async () => {
    if (!canJoin) return;
    const cleanRanges = isRange
      ? rows
          .filter((r) => isRowValid(r, maxPage))
          .map((r) => ({ startPage: Number(r.startPage), endPage: Number(r.endPage) }))
      : undefined;
    try {
      await joinByCode({
        code,
        pageRanges: cleanRanges,
        amount: isRange ? undefined : amountNum,
      }).unwrap();
      toast.success(t("hatim_join_code_success"));
      onClose && onClose();
    } catch (err) {
      const status = err?.status;
      const msg = err?.message || err?.data?.message || "";
      if (status === 400 && /üye/i.test(msg)) {
        setSubmitError(t("hatim_join_code_already_member"));
      } else {
        setSubmitError(msg || t("hatim_join_code_error"));
      }
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ fontWeight: 500 }}>{t("hatim_join_code_modal_title")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label={t("hatim_join_code_label")}
          placeholder={t("hatim_join_code_placeholder")}
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setSubmitError("");
          }}
          inputProps={{ maxLength: 8, style: { textTransform: "uppercase", letterSpacing: 2, fontFamily: "monospace" } }}
          error={(!!raw && !codeValid) || !!lookupError}
          helperText={helperText}
          margin="normal"
        />
        {codeValid && isFetching ? (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">…</Typography>
          </Box>
        ) : null}
        {info ? (
          <Box
            mt={2}
            p={2}
            sx={{
              borderRadius: 2,
              backgroundColor: dark ? "rgba(255,255,255,0.06)" : "#F1F8F8",
              border: dark ? "1px solid rgba(255,255,255,0.14)" : "1px solid #D6EBEB",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {info.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {info.type}
              {info.participation?.maxCount
                ? ` · ${t("hatim_join_code_preview_max").replace(/\{n\}/g, info.participation.maxCount)}`
                : ""}
            </Typography>
            <Box mt={2}>
              {isRange ? (
                <PageRangeEditor
                  assignedRanges={null}
                  rows={rows}
                  onChange={setRows}
                  maxPage={maxPage}
                />
              ) : (
                <TextField
                  type="number"
                  fullWidth
                  label={t("hatim_join_code_amount_label")}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{
                    min: 1,
                    max: info.participation?.maxCount || undefined,
                  }}
                />
              )}
            </Box>
          </Box>
        ) : null}
        {submitError ? (
          <Typography color="error" variant="body2" mt={2}>
            {submitError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton tone="cancel" onClick={onClose}>{t("hatim_join_code_cancel")}</SoftButton>
        <SoftButton tone="primary" onClick={handleSubmit} disabled={!canJoin}>
          {t("hatim_join_code_submit")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
